/**
 * @file This file contains static content for the activity.
 */
import {
  feetToMeters,
  degreesToRadians,
  sexagesimalToDecimal
} from "@/helpers/surveyingUtils";

/**
 * The acceptable error for each measurement step
 * @typedef {Object} ErrorMeasurementTolerance
 * @property {number} MP - The acceptable error for the MP measurement
 * @property {number} CS2 - The acceptable error for the CS2 measurement
 */
const ErrorMeasurementTolerance = {
  // acceptable error is 1 inch
  MP: feetToMeters(0.2),
  // acceptable error is 0.4 of a foot
  CS2: feetToMeters(0.4)
};

/**
 * The ideal maximum acceptable error for each measurement step
 * This is only used for display purposes, and is not used in any calculations
 * Units are ft, since the lab handout is in feet
 * @typedef {Object} IdealMeasurementTolerance
 * @property {number} MP - The ideal error for the MP measurement in feet
 * @property {number} CT2 - The ideal error for the CT2 measurement in feet
 */
const IdealMeasurementTolerance = {
  MP: 0.1,
  CS2: 0.2
};

/**
 * Constants for the curve, in feet, as displayed in the lab handout
 * @todo provide link to activity handout
 * @readonly
 * @property {number} Ts - The length between PI and T.S. in feet
 * @property {number} Es - The length between PI and MP in feet
 */
const DisplayedCurveConstants = {
  Ts: 254.19,
  Es: 20.51
  // TODO: determine if we need to display I angle value (24°40')
};

/**
 * Constants for the curve, converted to meters and radians for the simulation
 * Values are taken from the lab handout
 * @todo provide link to activity handout
 * @readonly
 * @property {number} I - Central angle in radians
 * @property {number} Ts - The length between PI and T.S. in meters
 * @property {number} Es - The length between PI and MP in meters
 * @property {number} A - The deflection of S.C. from the previous step's table
 */
const CurveConstants = {
  I: degreesToRadians(sexagesimalToDecimal(24, 40, 0)),
  Ts: feetToMeters(DisplayedCurveConstants.Ts),
  Es: feetToMeters(DisplayedCurveConstants.Es),
  A: degreesToRadians(sexagesimalToDecimal(1, 45, 0))
};

/**
 * Enum for all point names
 * @readonly
 * @enum {string}
 */
const PointName = {
  PI: "PI",
  // Equivalent to back tangent marker in the simple curve lab
  Z: "BACK_TANGENT_MARKER",
  TS: "TS",
  ST: "ST",
  TP1: "TP1",
  TP2: "TP2",
  TP3: "TP3",
  TP4: "TP4",
  SC: "SC",
  TP5: "TP5",
  TP6: "TP6",
  MP: "MP",
  TP7: "TP7",
  TP8: "TP8",
  CS: "CS",
  CS2: "CS2",
  TP9: "TP9",
  TP10: "TP10",
  TP11: "TP11"
};

/**
 * The map containing the layout of all the midpoint stakes
 * @typedef {Object.<string, StakePoint>} MidpointStakeLayout
 */

/**
 * Coordinates of a stake point, relative to it's origin point, zeroed to the reference point
 * @typedef {Object} StakePoint
 * @property {number} DEFLECTION - The deflection angle in radians
 * @property {number} DISTANCE - The distance in meters
 */

/** @type {MidpointStakeLayout} */
const MidpointStakeLayout = {
  // Points Measured from TS
  [PointName.TP1]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(0, 4, 12)),
    DISTANCE: feetToMeters(30)
  },
  [PointName.TP2]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(0, 16, 48)),
    DISTANCE: feetToMeters(60)
  },
  [PointName.TP3]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(0, 37, 48)),
    DISTANCE: feetToMeters(90)
  },
  [PointName.TP4]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(1, 7, 17)),
    DISTANCE: feetToMeters(120)
  },
  [PointName.SC]: {
    DEFLECTION: CurveConstants.A,
    DISTANCE: feetToMeters(149.94)
  },
  // Points measured from SC
  [PointName.TP5]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(0, 47, 24)),
    DISTANCE: feetToMeters(22.57)
  },
  [PointName.TP6]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(2, 32, 24)),
    DISTANCE: feetToMeters(72.55)
  },
  [PointName.MP]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(3, 32, 30)),
    DISTANCE: feetToMeters(101.13)
  },
  [PointName.TP7]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(4, 17, 24)),
    DISTANCE: feetToMeters(122.46)
  },
  [PointName.TP8]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(6, 2, 24)),
    DISTANCE: feetToMeters(172.25)
  },
  [PointName.CS]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(7, 5, 0)),
    DISTANCE: feetToMeters(201.87)
  },
  // Points Measured from ST
  [PointName.TP9]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(358, 41, 22)),
    DISTANCE: feetToMeters(129.81)
  },
  [PointName.TP10]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(359, 30, 16)),
    DISTANCE: feetToMeters(79.81)
  },
  [PointName.TP11]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(359, 55, 51)),
    DISTANCE: feetToMeters(29.81)
  },
  [PointName.CS2]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(358, 15, 0)),
    DISTANCE: feetToMeters(149.81)
  },
  // Points Measured from PI
  [PointName.TS]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(0, 24, 40)),
    DISTANCE: feetToMeters(CurveConstants.Ts)
  },
  [PointName.ST]: {
    DEFLECTION: degreesToRadians(sexagesimalToDecimal(204, 40, 0)),
    DISTANCE: feetToMeters(CurveConstants.Ts)
  }
};

/**
 * Enum for step IDs
 * @readonly
 * @enum {string}
 */
const StepId = {
  ZERO_INSTRUMENT_AT_PI: "ZERO_INSTRUMENT_AT_PI",
  STAKE_OUT_TS: "STAKE_OUT_TS",
  STAKE_OUT_ST: "STAKE_OUT_ST",
  ZERO_INSTRUMENT_AT_TS: "ZERO_INSTRUMENT_AT_TS",
  STAKE_OUT_TP1: "STAKE_OUT_TP1",
  STAKE_OUT_TP2: "STAKE_OUT_TP2",
  STAKE_OUT_TP3: "STAKE_OUT_TP3",
  STAKE_OUT_TP4: "STAKE_OUT_TP4",
  STAKE_OUT_SC: "STAKE_OUT_SC",
  ZERO_INSTRUMENT_AT_SC: "ZERO_INSTRUMENT_AT_SC",
  STAKE_OUT_TP5: "STAKE_OUT_TP5",
  STAKE_OUT_TP6: "STAKE_OUT_TP6",
  STAKE_OUT_MP: "STAKE_OUT_MP",
  MEASURE_ERROR_MP: "MEASURE_ERROR_MP",
  STAKE_OUT_TP7: "STAKE_OUT_TP7",
  STAKE_OUT_TP8: "STAKE_OUT_TP8",
  STAKE_OUT_CS: "STAKE_OUT_CS",
  ZERO_INSTRUMENT_AT_ST: "ZERO_INSTRUMENT_AT_ST",
  STAKE_OUT_TP9: "STAKE_OUT_TP9",
  STAKE_OUT_TP10: "STAKE_OUT_TP10",
  STAKE_OUT_TP11: "STAKE_OUT_TP11",
  STAKE_OUT_CS2: "STAKE_OUT_CS2",
  MEASURE_ERROR_CS2: "MEASURE_ERROR_CS2"
};

/**
 * The Static content driving the steps of each section.
 * @typedef {Object} Step
 * @property {string} id - The id of the step
 * @property {string} message - The text content of the step
 * @property {string} [instructions] - The instructions for the step
 */

/**
 * The Static content driving the sections of the activity.
 * @typedef {SectionContent}
 * @property {string} title - The title of the section
 * @property {string} [body] - The body text of the section
 * @property {string} [instructions] - Instructions for the section
 * @property {Step[]} steps - The steps of the section
 */

/** @type {SectionContent[]} */
const sections = [
  {
    title: "Setup at Point PI",
    body: `The total station has been placed at point PI.`,
    warning:
      "Click the help icon in the top right corner to view the controls for this activity.",
    showWarningFor: [
      StepId.ZERO_INSTRUMENT_AT_PI,
      StepId.STAKE_OUT_TS,
      StepId.STAKE_OUT_ST
    ],
    steps: [
      {
        id: StepId.ZERO_INSTRUMENT_AT_PI,
        message: `Zero the Instrument against the back tangent marker.`,
        instructions:
          "Use the <strong>0</strong> key on the keyboard to zero the instrument"
      },
      {
        id: StepId.STAKE_OUT_TS,
        message: `Stake out the TS using the tangent length (Ts) of ${DisplayedCurveConstants.Ts} feet, and mark the precise location with a nail on the stake.`,
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point T.S. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_ST,
        message: `Stake out the ST using tangent length (Ts) of ${DisplayedCurveConstants.Ts} feet and deflection angle (180°+I = 204°40').<br>Then mark the precise location with a nail on the stake.`,
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point CT. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      }
    ]
  },
  {
    title: "Setup at Point TS",
    body: `The total station has been placed at point TS.`,
    warning:
      'Click the <span class="badge bg-secondary">Data Tables</span> button in the top right corner to view the deflection angles and distances for the different points.',
    showWarningFor: [
      StepId.ZERO_INSTRUMENT_AT_TS,
      StepId.STAKE_OUT_TP1,
      StepId.STAKE_OUT_TP2,
      StepId.STAKE_OUT_TP3,
      StepId.STAKE_OUT_TP4
    ],
    steps: [
      {
        id: StepId.ZERO_INSTRUMENT_AT_TS,
        // TODO: ask about wording, lab document says "Set backsight 00°00’00” to PI"
        message: "Zero the Instrument against PI.",
        instructions:
          "Use the <strong>0</strong> key on the keyboard to zero the instrument"
      },
      {
        id: StepId.STAKE_OUT_TP1,
        message: "Stake out point 1",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point 1. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP2,
        message: "Stake out point 2",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point 2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP3,
        message: "Stake out point 3",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point 3. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP4,
        message: "Stake out point 4",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point 4. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_SC,
        message:
          "Stake out point SC and mark the precise location with a nail on the stake.",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point SC. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      }
    ]
  },
  {
    title: "Setup at Point SC",
    steps: [
      {
        id: StepId.ZERO_INSTRUMENT_AT_SC,
        message:
          "Set backsight (180-2A) to TS [A is the deflection of S.C. from the previous section's table]",
        instructions:
          "Use the <strong>2</strong> key on the keyboard to zero the instrument at 180° - 2A from the current orientation"
      },
      {
        id: StepId.STAKE_OUT_TP5,
        message: "Stake out point 5",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point 1. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP6,
        message: "Stake out point 6",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point 6. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_MP,
        message:
          "Stake out point MP and mark the precise location with a nail on the stake.",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> keys to instruct the prism holder to move the prism to point MP. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.MEASURE_ERROR_MP,
        message: `Measure the distance from MP to PI and compare it to the calculated distance, ${DisplayedCurveConstants.Es} ft.`,
        instructions:
          "The measurement will be displayed in a white label between MP and PI. If the error is greater than the acceptable error, you will need to adjust one or more of the stake points. <br><br>To navigate to a previous step, deselect the checkbox for the desired step.<br><br>The measurement will be hidden once this step is marked completed. Deselect the checkbox to see the measurement again."
      },
      {
        id: StepId.STAKE_OUT_TP7,
        message: "Stake out point 7",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TTP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP8,
        message: "Stake out point 8",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TTP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_CS,
        message:
          "Stake out point CS and mark the precise location with a nail on the stake",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TTP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      }
    ]
  },
  {
    title: "Setup at Point ST",
    warning:
      "The original CS placement has been hidden while you determine its location from this position.",
    showWarningFor: [StepId.STAKE_OUT_CS2],
    steps: [
      {
        id: StepId.ZERO_INSTRUMENT_AT_ST,
        message: "Set backsight 00°00’00” to PI ",
        instructions:
          "Use the <strong>0</strong> key on the keyboard to zero the instrument"
      },
      {
        id: StepId.STAKE_OUT_TP11,
        message: "Stake out point 11",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TTP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP10,
        message: "Stake out point 10",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TTP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_TP9,
        message: "Stake out point 9",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TTP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.STAKE_OUT_CS2,
        message:
          "Re-measure the point CS from your current position, and mark the precise location with a nail.",
        instructions:
          "Use the <strong>I</strong> (back), <strong>J</strong> (left), <strong>K</strong> (forward), and <strong>L</strong> (right) keys to instruct the prism holder to move the prism to point TTP2. Press the <strong>P</strong> key to have them place and nail the stake.<br><br>Hold the <strong>Shift</strong> key while pressing the <strong>I</strong>, <strong>J</strong>, <strong>K</strong>, and <strong>L</strong> keys to have the prism holder move in smaller increments."
      },
      {
        id: StepId.MEASURE_ERROR_CS2,
        message:
          "Measure the error distance between the original and re-staked point CS.",
        instructions:
          "The measurement will be displayed in a white label between the original and re-measured CT points. If the error is greater than the acceptable error, you will need to adjust one or more of the stake points.<br><br>To navigate to a previous step, deselect the checkbox for the desired step.<br><br>The measurement will be hidden once this step is marked completed. Deselect the checkbox to see the measurement again."
      }
    ]
  }
];

export {
  sections,
  StepId,
  PointName,
  CurveConstants,
  DisplayedCurveConstants,
  MidpointStakeLayout,
  ErrorMeasurementTolerance,
  IdealMeasurementTolerance
};
